import { UnknownElementPlaceholder } from './unknown-element-placeholder.js'
import type { TunaEditor } from '@tunasong/plugin-lib'
import type { FC, JSX, ReactNode } from 'react'
import type { RenderElementProps } from 'slate-react'

/** What renders an element whose type no loaded plugin handles */
export interface UnknownElementProps {
  type: string
  element: RenderElementProps['element']
  /** Among text, inside a paragraph or line, rather than among blocks (Slate keeps an element's children all one kind) */
  inline: boolean
  children?: ReactNode
}

interface TreeNode {
  text?: unknown
  children?: TreeNode[]
}

/** Whether `element` sits among text: found by identity in the document, so nothing depends on render bookkeeping */
const isAmongText = (nodes: TreeNode[], element: unknown): boolean => {
  if (nodes.includes(element as TreeNode)) {
    return nodes.some(node => typeof node.text === 'string')
  }
  return nodes.some(node => Array.isArray(node.children) && isAmongText(node.children, element))
}


/**
 * Fallback `renderElement` for the editor's `<Plate>` component.
 *
 * Plate runs each plugin's element renderer in order. When no plugin claims the
 * element's `type`, this fallback fires (instead of Plate's default
 * `<PlateElement>` `<div>`). For unregistered types we render a visible
 * UnknownElementPlaceholder so the user (and devs) can see something is there
 * — instead of silently dropping the data.
 *
 * For elements that arrive here without being claimed by a plugin but whose
 * type IS registered (rare — usually means the plugin omitted `node.component`),
 * we render a neutral wrapper that mirrors Plate's default behavior.
 *
 * `Unknown` replaces the placeholder: the mobile reader shows its fallback block there (#3042).
 */
export const renderUnknownElement =
  (editor: TunaEditor, Unknown?: FC<UnknownElementProps>) =>
  (props: RenderElementProps): JSX.Element => {
    const { element, attributes, children } = props
    const type = (element as { type?: unknown }).type
    const isUnknown = (editor as { isUnknownType?: (t: string) => boolean }).isUnknownType
    if (typeof type === 'string' && type.length > 0 && isUnknown && isUnknown(type)) {
      return (
        <span {...attributes}>
          {Unknown ? (
            <Unknown type={type} element={element} inline={isAmongText(editor.children as TreeNode[], element)}>
              {children}
            </Unknown>
          ) : (
            <UnknownElementPlaceholder type={type}>{children}</UnknownElementPlaceholder>
          )}
        </span>
      )
    }
    // Registered type but no plugin renderer claimed it (rare). Render a neutral
    // wrapper that preserves slate-react's `attributes` so node bookkeeping stays
    // intact.
    const isInline = attributes['data-slate-inline'] === true
    if (isInline) {
      return <span {...attributes}>{children}</span>
    }
    return <div {...attributes}>{children}</div>
  }
